import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import { MeaningCheckFn } from '../meaningfulnessPredicates';
import { decomposeProperties } from '../decomposer';
import { checkContextMatch } from './helpers';
import { getPossibleCssProperties } from '../../../../../../../ui/globalDesignPanel/common/grouping/propertyRelationships';
import { STYLE_NAMESPACE, StyleNamespace, Styles } from '../../types';

/**
 * Result of action visibility check
 */
export interface ActionVisibilityResult {
  shouldShow: boolean;
  reason?: string;
}

/**
 * Checks if the context has a meaningful value for any of the allowed properties
 *
 * @param context - The context to check
 * @param allowedStyles - Set of allowed CSS property types
 * @param meaningCheck - Function to determine if a value is meaningful
 * @returns true if any meaningful value exists
 */
export const hasAnyMeaningfulValueCheckContext = (
  context: VirtualManifestAPI,
  allowedStyles: Set<string>,
  meaningCheck: MeaningCheckFn,
  namespace?: StyleNamespace,
  allowedProperties?: Set<string>,
): boolean => {
  const styles = context.query.getStyles();
  const styleDefinitions = context.resolver.getStyleDefinitions();

  // Check CSS properties
  if (
    styles?.cssProperties &&
    (!namespace || namespace === STYLE_NAMESPACE.CssProperties)
  ) {
    const possibleCssProperties = getPossibleCssProperties(
      allowedProperties
        ? Array.from(allowedProperties)
        : Object.keys(styleDefinitions.cssProperties),
    );
    const filteredStylesValues = Object.fromEntries(
      Object.entries(styles.cssProperties).filter(([propType]) =>
        possibleCssProperties.has(propType),
      ),
    );

    // Decompose all properties to lowest atomics recursively
    const decomposed = decomposeProperties(filteredStylesValues);

    // Check if any decomposed atomic is in allowedStyles and has meaningful value
    for (const [atomicProperty, atomicValue] of Object.entries(decomposed)) {
      if (allowedStyles.has(atomicProperty)) {
        if (meaningCheck(atomicValue, atomicProperty)) {
          return true;
        }
      }
    }
  }

  // Check CSS custom properties
  if (
    styles?.cssCustomProperties &&
    styleDefinitions.cssCustomProperties &&
    (!namespace || namespace === STYLE_NAMESPACE.CssCustomProperties)
  ) {
    // Build a map of cssPropertyType -> value for decomposition
    const customPropsToDecompose: Record<string, string> = {};
    for (const [styleId, value] of Object.entries(styles.cssCustomProperties)) {
      const cssPropertyType =
        styleDefinitions.cssCustomProperties[styleId]?.cssPropertyType;
      if (
        cssPropertyType &&
        (!allowedProperties || allowedProperties?.has(styleId))
      ) {
        customPropsToDecompose[cssPropertyType] = value;
      }
    }

    // Decompose all custom properties to lowest atomics recursively
    const decomposed = decomposeProperties(customPropsToDecompose);

    // Check if any decomposed atomic is in allowedStyles and has meaningful value
    for (const [atomicProperty, atomicValue] of Object.entries(decomposed)) {
      if (allowedStyles.has(atomicProperty)) {
        if (meaningCheck(atomicValue, atomicProperty)) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Checks if any element in the hierarchy has a meaningful value
 * for any of the allowed properties
 *
 * @param rootContext - The root manifest context
 * @param allowedStyles - Set of allowed CSS property types
 * @param meaningCheck - Function to determine if a value is meaningful
 * @returns true if any meaningful value exists
 */
export const hasAnyMeaningfulValue = (
  rootContext: VirtualManifestAPI,
  allowedStyles: Set<string>,
  meaningCheck: MeaningCheckFn,
): boolean => {
  return checkContextMatch(rootContext, (context) =>
    hasAnyMeaningfulValueCheckContext(context, allowedStyles, meaningCheck),
  );
};

/**
 * Checks if the element's manifest has any of the allowed properties
 * (does NOT check inner elements)
 *
 * @param context - The root manifest context
 * @param allowedStyles - Set of allowed CSS property types
 * @returns true if root manifest has any allowed property
 */
export const isAllowedStylePresent = (
  context: VirtualManifestAPI,
  allowedStyles: Set<string>,
  namespace?: StyleNamespace,
): boolean => {
  const styleDefinitions = context.resolver.getStyleDefinitions();

  // Check CSS properties in manifest
  if (
    (!namespace || namespace === STYLE_NAMESPACE.CssProperties) &&
    styleDefinitions.cssProperties
  ) {
    for (const styleType of Object.keys(styleDefinitions.cssProperties)) {
      if (allowedStyles.has(styleType)) {
        return true;
      }
    }
  }

  // Check CSS custom properties in manifest
  if (
    (!namespace || namespace === STYLE_NAMESPACE.CssCustomProperties) &&
    styleDefinitions.cssCustomProperties
  ) {
    for (const customProp of Object.values(
      styleDefinitions.cssCustomProperties,
    )) {
      if (
        customProp.cssPropertyType &&
        allowedStyles.has(customProp.cssPropertyType)
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Checks if ONLY the root element has the property in manifest
 * (and no inner elements have it)
 *
 * @param rootContext - The root manifest context
 * @param allowedStyles - Set of allowed CSS property types
 * @returns true if only root has the property
 */
export const hasOnlyRootWithManifest = (
  rootContext: VirtualManifestAPI,
  allowedStyles: Set<string>,
): boolean => {
  // First check if root has the property
  if (!isAllowedStylePresent(rootContext, allowedStyles)) {
    return false;
  }

  // Check if any inner element has the property
  const checkInnerElements = (context: VirtualManifestAPI): boolean => {
    const children = context.getChildren();

    for (const child of children) {
      if (isAllowedStylePresent(child, allowedStyles)) {
        return true; // Found an inner element with the property
      }

      // Recursively check grandchildren
      if (checkInnerElements(child)) {
        return true;
      }
    }

    return false;
  };

  // If any inner element has the property, return false
  if (checkInnerElements(rootContext)) {
    return false;
  }

  // Only root has the property
  return true;
};
